import { IChannelList } from "@ts/common-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getClientChannels, updateRevstackChannels } from "@ts/retrieve-data";
import * as Sentry from "@sentry/react";

const channelSlice = createSlice({
    name: "channel",
    initialState: { value: 1 },
    reducers: {
        updateChannel: (state, action: PayloadAction<number>) => {
            state.value = action.payload;
            if (action.payload !== null && action.payload !== undefined) {
                Sentry.setTag("channel_id", action.payload.toString());
            } else {
                Sentry.setTag("channel_id", null);
            }
        },
    },
});

const channelWalletBalanceSlice = createSlice({
    name: "channelWalletBalance",
    initialState: { value: 0 },
    reducers: {
        updateChannelWalletBalance: (state, action: PayloadAction<number>) => {
            state.value = action.payload;
        },
    },
});

const channelCurrencySymbolSlice = createSlice({
    name: "channelCurrencySymbol",
    initialState: { value: "" },
    reducers: {
        updateChannelCurrencySymbol: (state, action: PayloadAction<string>) => {
            state.value = action.payload;
        },
    },
});

const channelDomainSlice = createSlice({
    name: "channelDomain",
    initialState: { value: "" },
    reducers: {
        updateChannelDomain: (state, action: PayloadAction<string>) => {
            state.value = action.payload;
        },
    },
});

// !Note: Benefit of using this is when we make api call no need to use its reducer for updating the state value.
// ! Also, values gets updated automatically, when we call its createAsyncThunk function using dispatch
// ! We can even extract value,loader status and error from it
// TODO: Research more on asyncThunk Middleware
// TODO: Change fetch() to axios() in the coming

const channelListSlice = createSlice({
    name: "channelList",
    initialState: {
        value: [],
        isLoading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getClientChannels.pending, (state: IChannelList) => {
            state.isLoading = true;
        });
        builder.addCase(
            getClientChannels.fulfilled,
            (state: IChannelList, action) => {
                state.isLoading = false;
                state.value = action.payload;
            }
        );
        builder.addCase(
            getClientChannels.rejected,
            (state: IChannelList, action) => {
                state.isLoading = false;
                state.error = action.error.message;
            }
        );
    },
});

/**
 * Revstack channel slice
 * - value: Array of selected channel IDs
 * - updateId: Timestamp that changes whenever channels are updated, used to trigger filter API calls
 */
const revstackChannelSlice = createSlice({
    name: "revstackChannel",
    initialState: { value: [], updateId: 0 },
    reducers: {
        updateRevstackChannel: (state, action: PayloadAction<number[]>) => {
            state.value = action.payload;
            state.updateId = Date.now();

            if (action.payload !== null && action.payload !== undefined) {
                Sentry.setTag("revstack_channel_id", action.payload.toString());
            } else {
                Sentry.setTag("revstack_channel_id", null);
            }
        },
    },
});

/**
 * Revstack channels list slice
 * - value: Array of all available channels for the current client
 * - updateId: Timestamp that changes when channels are fetched, used to detect when new channels are loaded
 */
const revstackChannelsListSlice = createSlice({
    name: "revstackChannelsList",
    initialState: {
        value: [],
        isLoading: false,
        error: null,
        updateId: 0,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(updateRevstackChannels.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(updateRevstackChannels.fulfilled, (state, action) => {
            state.isLoading = false;
            state.value = action.payload;
            state.error = null;
            state.updateId = Date.now();
        });
        builder.addCase(updateRevstackChannels.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload as string;
        });
    },
});

export const { updateChannel } = channelSlice.actions;
export const { updateChannelWalletBalance } = channelWalletBalanceSlice.actions;
export const { updateChannelCurrencySymbol } =
    channelCurrencySymbolSlice.actions;
export const { updateChannelDomain } = channelDomainSlice.actions;
export const { updateRevstackChannel } = revstackChannelSlice.actions;

export const channelReducer = channelSlice.reducer;
export const channelListReducer = channelListSlice.reducer;
export const channelWalletBalanceReducer = channelWalletBalanceSlice.reducer;
export const channelCurrencySymbolReducer = channelCurrencySymbolSlice.reducer;
export const channelDomainReducer = channelDomainSlice.reducer;
export const revstackChannelReducer = revstackChannelSlice.reducer;
export const revstackChannelsListReducer = revstackChannelsListSlice.reducer;
